/** `snippets/product-info.liquid` — `[data-product-info]`: variant-driven price
 *  updates, quantity stepper, subscribe vs buy-once (selling plan hidden input),
 *  accordion panels, and region-based shipping availability on the product meta column.
 */

const REGION_LS_KEY = 'wyld_us_state'

const ROOT_SELECTOR = '[data-product-info]'
const VARIANT_SELECT_SELECTOR = '[data-product-info-variant-select]'
const QTY_INPUT_SELECTOR = '[data-product-info-qty]'
const QTY_MINUS_SELECTOR = '[data-product-info-qty-minus]'
const QTY_PLUS_SELECTOR = '[data-product-info-qty-plus]'
const QTY_DISPLAY_SELECTOR = '[data-product-info-qty-display]'
const PRICE_SELECTOR = '[data-product-info-price]'
const PACK_LINE_SELECTOR = '[data-product-info-pack-line]'
const SELLING_PLAN_SELECTOR = '[data-product-info-selling-plan]'
const PURCHASE_RADIO_SELECTOR = '[data-product-info-purchase-radio]'
const PURCHASE_ONCE_SELECTOR = '[data-product-info-purchase-once]'
const PURCHASE_SUBSCRIBE_SELECTOR = '[data-product-info-purchase-subscribe]'
const ONCE_PRICE_SELECTOR = '[data-product-info-once-price]'
const SUBSCRIBE_COMPARE_SELECTOR = '[data-product-info-subscribe-compare]'
const SUBSCRIBE_PRICE_SELECTOR = '[data-product-info-subscribe-price]'
const SUBSCRIBE_BADGE_SELECTOR = '[data-product-info-subscribe-badge]'
const CADENCE_SELECTOR = '[data-product-info-cadence]'
const CADENCE_SELECT_SELECTOR = '[data-product-info-cadence-select]'
const ADD_BTN_SELECTOR = '[data-product-info-add-btn]'
const ACCORDION_TRIGGER_SELECTOR = '[data-product-info-accordion-trigger]'
const FORM_SELECTOR = '[data-ajax-product-form]'
const LOCATOR_SELECTOR = '[data-product-info-locator]'
const AVAILABILITY_SELECTOR = '[data-product-availability]'
const AVAILABILITY_MESSAGE_SELECTOR = '[data-product-availability-message]'
const REVIEW_ROW_SELECTOR = '[data-product-info-review-row]'
const REVIEWS_TARGET_SELECTOR = '.product-reviews'

function formatMoney(cents, currency) {
  try {
    return new Intl.NumberFormat(undefined, {
      style: 'currency',
      currency: currency || 'USD',
    }).format(Number(cents) / 100)
  } catch {
    return `${(Number(cents) / 100).toFixed(2)} ${currency || ''}`.trim()
  }
}

function parseVariantPayload(root) {
  const script = root.querySelector('script[type="application/json"]')
  if (!script?.textContent) return { variants: [] }
  try {
    return JSON.parse(script.textContent)
  } catch {
    return { variants: [] }
  }
}

function initProductInfo(root) {
  const currency = root.dataset.currency || 'USD'
  const { variants } = parseVariantPayload(root)
  if (!variants.length) return

  const variantSelect = root.querySelector(VARIANT_SELECT_SELECTOR)
  const qtyInput = root.querySelector(QTY_INPUT_SELECTOR)
  const qtyMinus = root.querySelector(QTY_MINUS_SELECTOR)
  const qtyPlus = root.querySelector(QTY_PLUS_SELECTOR)
  const qtyDisplay = root.querySelector(QTY_DISPLAY_SELECTOR)
  const priceEl = root.querySelector(PRICE_SELECTOR)
  const packLineEl = root.querySelector(PACK_LINE_SELECTOR)
  const sellingPlanInput = root.querySelector(SELLING_PLAN_SELECTOR)
  const purchaseRadios = root.querySelectorAll(PURCHASE_RADIO_SELECTOR)
  const onceLabel = root.querySelector(PURCHASE_ONCE_SELECTOR)
  const subscribeLabel = root.querySelector(PURCHASE_SUBSCRIBE_SELECTOR)
  const oncePriceEl = root.querySelector(ONCE_PRICE_SELECTOR)
  const subscribeCompareEl = root.querySelector(SUBSCRIBE_COMPARE_SELECTOR)
  const subscribePriceEl = root.querySelector(SUBSCRIBE_PRICE_SELECTOR)
  const subscribeBadgeEl = root.querySelector(SUBSCRIBE_BADGE_SELECTOR)
  const cadenceEl = root.querySelector(CADENCE_SELECTOR)
  const cadenceSelect = root.querySelector(CADENCE_SELECT_SELECTOR)
  const addBtn = root.querySelector(ADD_BTN_SELECTOR)

  const getVariantId = () => {
    if (variantSelect) return Number(variantSelect.value, 10)
    const hiddenId = root.querySelector('form.product-info-form input[name="id"]')
    return Number(hiddenId?.value, 10)
  }

  const findVariant = (id) => variants.find((v) => v.id === id)

  // The selling plan chosen in the cadence <select> (e.g. every month vs. every
  // three months). Falls back to the first allocation when no match is found.
  const getSelectedAllocation = (variant) => {
    const allocations = variant?.allocations
    if (!allocations?.length) return null
    if (cadenceSelect?.value) {
      const planId = Number(cadenceSelect.value, 10)
      const match = allocations.find((a) => a.plan_id === planId)
      if (match) return match
    }
    return allocations[0]
  }

  const rebuildCadenceOptions = (variant) => {
    if (!cadenceSelect) return
    const previous = cadenceSelect.value
    const allocations = variant?.allocations || []
    cadenceSelect.innerHTML = ''
    allocations.forEach((alloc) => {
      const option = document.createElement('option')
      option.value = String(alloc.plan_id)
      option.dataset.price = String(alloc.price)
      option.textContent = alloc.name || 'Subscription'
      cadenceSelect.appendChild(option)
    })
    if (allocations.some((a) => String(a.plan_id) === previous)) {
      cadenceSelect.value = previous
    }
  }

  const updateCadenceVisibility = () => {
    if (!cadenceEl) return
    const selected = root.querySelector(`${PURCHASE_RADIO_SELECTOR}:checked`)
    cadenceEl.hidden = selected?.value !== 'subscribe'
  }

  const syncSellingPlanFromPurchaseMode = () => {
    if (!sellingPlanInput) return
    const selected = root.querySelector(`${PURCHASE_RADIO_SELECTOR}:checked`)
    const mode = selected?.value
    const variant = findVariant(getVariantId())
    const alloc = getSelectedAllocation(variant)
    if (mode === 'subscribe' && alloc) {
      sellingPlanInput.value = String(alloc.plan_id)
    } else {
      sellingPlanInput.value = ''
    }
  }

  const setPurchaseActiveClass = () => {
    const selected = root.querySelector(`${PURCHASE_RADIO_SELECTOR}:checked`)
    const mode = selected?.value
    onceLabel?.classList.toggle('product-info-purchase-option-active', mode === 'once')
    subscribeLabel?.classList.toggle('product-info-purchase-option-active', mode === 'subscribe')
  }

  const updateSubscribeBadge = (variant, allocation) => {
    if (!subscribeBadgeEl) return
    const alloc = allocation || getSelectedAllocation(variant)
    if (!alloc || !variant?.price) {
      subscribeBadgeEl.hidden = true
      subscribeBadgeEl.textContent = ''
      return
    }
    const diff = variant.price - alloc.price
    if (diff <= 0) {
      subscribeBadgeEl.hidden = true
      subscribeBadgeEl.textContent = ''
      return
    }
    const pct = Math.round((diff / variant.price) * 100)
    subscribeBadgeEl.hidden = false
    subscribeBadgeEl.textContent = `save ${pct}%`
  }

  const applyVariant = (variantId) => {
    const variant = findVariant(variantId)
    if (!variant) return

    if (priceEl) {
      priceEl.textContent = formatMoney(variant.price, currency)
    }
    if (packLineEl) {
      const unitQuantity = variant.unit_quantity || ''
      packLineEl.textContent = unitQuantity
      packLineEl.classList.toggle('product-info-pack-line-placeholder', !unitQuantity)
    }
    if (oncePriceEl) {
      oncePriceEl.textContent = formatMoney(variant.price, currency)
    }
    if (subscribeCompareEl) {
      subscribeCompareEl.textContent = formatMoney(variant.price, currency)
    }

    rebuildCadenceOptions(variant)
    const alloc = getSelectedAllocation(variant)
    if (subscribePriceEl) {
      subscribePriceEl.textContent = alloc ? formatMoney(alloc.price, currency) : ''
    }

    updateSubscribeBadge(variant, alloc)

    updateAddButton()

    const subscribeRadio = root.querySelector(
      `${PURCHASE_RADIO_SELECTOR}[value="subscribe"]`,
    )
    if (subscribeRadio && !alloc) {
      subscribeRadio.checked = false
      const onceRadio = root.querySelector(`${PURCHASE_RADIO_SELECTOR}[value="once"]`)
      if (onceRadio) onceRadio.checked = true
    }

    syncSellingPlanFromPurchaseMode()
    setPurchaseActiveClass()
    updateCadenceVisibility()
  }

  const dispatchVariantChanged = (variantId) => {
    const variant = findVariant(variantId)
    document.dispatchEvent(
      new CustomEvent('theme:variant:changed', {
        detail: { variantId, mediaId: variant?.featured_media_id ?? null },
      }),
    )
  }

  if (variantSelect) {
    variantSelect.addEventListener('change', () => {
      const variantId = Number(variantSelect.value, 10)
      applyVariant(variantId)
      dispatchVariantChanged(variantId)
    })
  }

  purchaseRadios.forEach((radio) => {
    radio.addEventListener('change', () => {
      syncSellingPlanFromPurchaseMode()
      setPurchaseActiveClass()
      updateCadenceVisibility()
    })
  })

  cadenceSelect?.addEventListener('change', () => {
    const variant = findVariant(getVariantId())
    const alloc = getSelectedAllocation(variant)
    if (subscribePriceEl) {
      subscribePriceEl.textContent = alloc ? formatMoney(alloc.price, currency) : ''
    }
    updateSubscribeBadge(variant, alloc)
    syncSellingPlanFromPurchaseMode()
  })

  const syncQtyDisplay = () => {
    const n = Math.max(1, Math.min(99, Number(qtyInput?.value || 1, 10)))
    if (qtyInput) qtyInput.value = String(n)
    if (qtyDisplay) qtyDisplay.textContent = String(n)
  }

  qtyMinus?.addEventListener('click', () => {
    if (!qtyInput) return
    const n = Number(qtyInput.value || 1, 10)
    qtyInput.value = String(Math.max(1, n - 1))
    syncQtyDisplay()
  })

  qtyPlus?.addEventListener('click', () => {
    if (!qtyInput) return
    const n = Number(qtyInput.value || 1, 10)
    qtyInput.value = String(Math.min(99, n + 1))
    syncQtyDisplay()
  })

  syncQtyDisplay()

  root.querySelectorAll(ACCORDION_TRIGGER_SELECTOR).forEach((trigger) => {
    trigger.addEventListener('click', () => {
      const expanded = trigger.getAttribute('aria-expanded') === 'true'
      const panelId = trigger.getAttribute('aria-controls')
      const panel = panelId ? document.getElementById(panelId) : null
      trigger.setAttribute('aria-expanded', expanded ? 'false' : 'true')
      if (panel) togglePanel(panel, !expanded)
    })
  })

  // ── Smooth-scroll from the rating row to the reviews section ───────
  const reviewRow = root.querySelector(REVIEW_ROW_SELECTOR)
  if (reviewRow && reviewRow.getAttribute('role') === 'button') {
    const scrollToReviews = () => {
      const target = document.querySelector(REVIEWS_TARGET_SELECTOR)
      if (target) target.scrollIntoView({ behavior: 'smooth', block: 'start' })
    }
    reviewRow.addEventListener('click', scrollToReviews)
    reviewRow.addEventListener('keydown', (e) => {
      if (e.key === 'Enter' || e.key === ' ' || e.key === 'Spacebar') {
        e.preventDefault()
        scrollToReviews()
      }
    })
  }

  // ── Region-based shipping availability ─────────────────────────────
  const formEl = root.querySelector(FORM_SELECTOR)
  const locatorEl = root.querySelector(LOCATOR_SELECTOR)
  const availMessageEl = root.querySelector(AVAILABILITY_MESSAGE_SELECTOR)
  const availEl = root.querySelector(AVAILABILITY_SELECTOR)
  const includedStates = availEl ? parseJsonAttr(availEl, 'data-included-states') : []

  let regionKnown = false
  let regionAvailable = true

  const currentVariantAvailable = () => {
    const v = findVariant(getVariantId())
    return v ? v.available : false
  }

  function updateAddButton() {
    if (!addBtn) return
    addBtn.disabled = !currentVariantAvailable() || (regionKnown && !regionAvailable)
  }

  const setShippingUiAvailable = (available) => {
    if (formEl) formEl.hidden = !available
    if (locatorEl) locatorEl.hidden = available
    if (availMessageEl) availMessageEl.hidden = available
  }

  function applyRegion(state) {
    if (!state) {
      regionKnown = false
      regionAvailable = true
      setShippingUiAvailable(true)
      if (addBtn) addBtn.disabled = true
      return
    }

    regionKnown = true
    regionAvailable = isAvailableInState(includedStates, state)

    if (!regionAvailable && state === 'California') {
      globalThis.location.href = '/'
      return
    }

    setShippingUiAvailable(regionAvailable)

    if (regionAvailable) {
      updateAddButton()
    } else if (addBtn) {
      addBtn.disabled = true
    }
  }

  applyVariant(getVariantId())

  const currentState = safeLocalStorageGet(REGION_LS_KEY)
  applyRegion(currentState)

  // IP-based detection runs in the header (site-wide) and broadcasts the
  // resolved state via `theme:region:state-changed`, which this listener picks up.
  document.addEventListener('theme:region:state-changed', (e) => {
    applyRegion(e.detail?.state || '')
  })
}

function togglePanel(panel, open) {
  if (open) {
    panel.classList.add('product-info-accordion-panel-open')
    // Animate from the current height (usually 0) up to the content height,
    // then release to `auto` so the panel can reflow if its content changes.
    panel.style.height = `${panel.scrollHeight}px`
    const onEnd = (e) => {
      if (e.propertyName !== 'height') return
      panel.style.height = 'auto'
      panel.removeEventListener('transitionend', onEnd)
    }
    panel.addEventListener('transitionend', onEnd)
  } else {
    // Pin the current pixel height (in case it is `auto`) before collapsing to 0
    // so the transition has two explicit endpoints to animate between.
    panel.style.height = `${panel.scrollHeight}px`
    // Force a reflow so the browser registers the pinned height as the start.
    panel.getBoundingClientRect()
    panel.classList.remove('product-info-accordion-panel-open')
    panel.style.height = '0px'
  }
}

function parseJsonAttr(el, attr) {
  const raw = el.getAttribute(attr)
  if (!raw) return []
  try {
    const parsed = JSON.parse(raw)
    return Array.isArray(parsed) ? parsed : []
  } catch {
    return []
  }
}

function isAvailableInState(includedStates, state) {
  if (includedStates.length === 0) return true
  return includedStates.includes(state)
}

function safeLocalStorageGet(key) {
  try {
    return localStorage.getItem(key)
  } catch {
    return null
  }
}

function initAllProductInfo() {
  document.querySelectorAll(ROOT_SELECTOR).forEach((root) => {
    initProductInfo(root)
  })
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initAllProductInfo)
} else {
  initAllProductInfo()
}
